import { useWorkspace } from '@/features/workspaces/hooks';
import { SelectOption } from '@/lib/Selects/Select/types';
import {
  DEFAULT_FILTER_IDS,
  PerformanceAudienceFilterOption,
  useGetCampaignPerformanceAudiencesQuery,
  useGetCampaignPerformanceSourcesQuery,
} from '@/services/endpoints/performanceAudience';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PerformanceAudienceFiltersState } from '../types';
import { mapCampaignPerformanceAudiencesToFilterOptions } from '../utils/campaignPerformanceAudiences';
import { mapCampaignPerformanceSourcesToFilterOptions } from '../utils/campaignPerformanceSources';

const STATIC_DATE_RANGES: PerformanceAudienceFilterOption[] = [
  { id: 'last_7_days', value: 'performanceAudience.filters.last_7_days' },
  { id: 'last_14_days', value: 'performanceAudience.filters.last_14_days' },
  { id: 'last_30_days', value: 'performanceAudience.filters.last_30_days' },
];

export const usePerformanceAudienceBootstrap = () => {
  const { id: workspaceId } = useWorkspace();

  const [filters, setFilters] = useState<PerformanceAudienceFiltersState>({
    audienceId: DEFAULT_FILTER_IDS.audienceId,
    googleScopeId: DEFAULT_FILTER_IDS.googleScopeId,
    metaScopeId: DEFAULT_FILTER_IDS.metaScopeId,
    dateRange: DEFAULT_FILTER_IDS.dateRange,
  });

  const {
    data: campaignPerformanceAudiences,
    isLoading: isCampaignPerformanceAudiencesLoading,
    isError: isCampaignPerformanceAudiencesError,
    refetch: refetchCampaignPerformanceAudiences,
  } = useGetCampaignPerformanceAudiencesQuery({ workspaceId }, { skip: !workspaceId });

  const {
    data: campaignPerformanceSources,
    isLoading: isCampaignPerformanceSourcesLoading,
    isError: isCampaignPerformanceSourcesError,
    refetch: refetchCampaignPerformanceSources,
  } = useGetCampaignPerformanceSourcesQuery({ workspaceId }, { skip: !workspaceId });

  const isBootstrapSettled = !isCampaignPerformanceAudiencesLoading && !isCampaignPerformanceSourcesLoading && !!workspaceId;

  const audienceFilterOptions = useMemo(() => mapCampaignPerformanceAudiencesToFilterOptions(campaignPerformanceAudiences, []), [campaignPerformanceAudiences]);

  const { googleScopes: mappedGoogleScopes, metaScopes: mappedMetaScopes } = useMemo(
    () =>
      mapCampaignPerformanceSourcesToFilterOptions(campaignPerformanceSources, {
        googleScopes: [],
        metaScopes: [],
      }),
    [campaignPerformanceSources]
  );

  const isInitialLoading = !!workspaceId && !isBootstrapSettled;

  const isFiltersReady = useMemo(() => {
    if (!isBootstrapSettled) return false;
    const audienceValid = !audienceFilterOptions.length || audienceFilterOptions.some((o) => o.id === filters.audienceId);
    const googleValid = !mappedGoogleScopes.length || mappedGoogleScopes.some((o) => o.id === filters.googleScopeId);
    const metaValid = !mappedMetaScopes.length || mappedMetaScopes.some((o) => o.id === filters.metaScopeId);
    return audienceValid && googleValid && metaValid;
  }, [isBootstrapSettled, audienceFilterOptions, mappedGoogleScopes, mappedMetaScopes, filters]);

  const isError = isCampaignPerformanceAudiencesError || isCampaignPerformanceSourcesError;

  const refetch = useCallback(() => {
    void refetchCampaignPerformanceAudiences();
    void refetchCampaignPerformanceSources();
  }, [refetchCampaignPerformanceAudiences, refetchCampaignPerformanceSources]);

  useEffect(() => {
    if (!audienceFilterOptions.length) {
      return;
    }

    const hasValidSelection = audienceFilterOptions.some((option) => option.id === filters.audienceId);

    if (hasValidSelection) {
      return;
    }

    setFilters((current) => ({ ...current, audienceId: audienceFilterOptions[0].id }));
  }, [audienceFilterOptions, filters.audienceId]);

  useEffect(() => {
    if (!mappedGoogleScopes.length) {
      return;
    }

    const hasValidSelection = mappedGoogleScopes.some((option) => option.id === filters.googleScopeId);

    if (hasValidSelection) {
      return;
    }

    setFilters((current) => ({ ...current, googleScopeId: mappedGoogleScopes[0].id }));
  }, [mappedGoogleScopes, filters.googleScopeId]);

  useEffect(() => {
    if (!mappedMetaScopes.length) {
      return;
    }

    const hasValidSelection = mappedMetaScopes.some((option) => option.id === filters.metaScopeId);

    if (hasValidSelection) {
      return;
    }

    setFilters((current) => ({ ...current, metaScopeId: mappedMetaScopes[0].id }));
  }, [mappedMetaScopes, filters.metaScopeId]);

  const setAudienceFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, audienceId: String(option.id) }));
  }, []);

  const setGoogleScopeFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, googleScopeId: String(option.id) }));
  }, []);

  const setMetaScopeFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, metaScopeId: String(option.id) }));
  }, []);

  const setDateRangeFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, dateRange: String(option.id) }));
  }, []);

  return {
    workspaceId,
    filters,
    isFiltersReady,
    filterOptions: {
      audiences: audienceFilterOptions,
      googleScopes: mappedGoogleScopes,
      metaScopes: mappedMetaScopes,
      dateRanges: STATIC_DATE_RANGES,
    },
    setAudienceFilter,
    setGoogleScopeFilter,
    setMetaScopeFilter,
    setDateRangeFilter,
    isInitialLoading,
    isError,
    refetch,
  };
};
